/**
 * kidChipMatch.ts — "Looks like this is for C." #1957 item 6.
 *
 * Decides which kid a schedule import belongs to, from the text on the image
 * and from whatever she types in the label box. The answer only ever
 * PRESELECTS a chip she can change, which is what makes a confident guess
 * affordable — but a WRONG confident guess silently re-colors a whole import,
 * so every rule here refuses rather than reaches.
 *
 * ── The one rule that matters: ambiguity means no guess ─────────────────────
 *
 * Two kids named Carson and Caleb share an initial. "C's School" cannot mean
 * one of them, so it must mean neither. Same for a prefix nickname ("Jo" for
 * both Jonah and Joss) and for two different kids named in one string. Every
 * match pass below collects ALL candidates and then returns null unless
 * exactly one kid survives — rather than returning the first hit, which is
 * how a tie silently becomes a wrong answer.
 *
 * ── Why word boundaries, everywhere ────────────────────────────────────────
 *
 * A single-letter initial is the most dangerous token in this file. Matched as
 * a substring, "J" hits "Jim", "January", "Jr" and the "j" in "subject" — so
 * an initial can only ever match as a whole word. The same guard keeps
 * "Jaxon" from matching inside "Jaxonville".
 *
 * ⚠️ Her kid names really may BE single letters — the legend renders
 * known_kid_names verbatim and hers reads "C, J, Family, Work". So the
 * name rule and the initial rule can be the same character, and the
 * possessive rule is what carries "C's School". Nothing here assumes a name is
 * longer than one letter.
 */

/** A family-memory fact: `subject` is who it is about, `value` is the text. */
export interface KidMemoryFact {
  subject: string | null;
  value: string;
}

/** How the kid was identified — drives nothing but the explanation line. */
export type KidMatchVia = "name" | "possessive" | "nickname" | "initial" | "memory";

export interface KidMatch {
  /** The kid's name exactly as it is stored, so it matches the chip and legend. */
  kid: string;
  via: KidMatchVia;
  /** The text that triggered it, for the "Looks like…" line. */
  evidence: string;
}

/** Shortest prefix that may stand in as a nickname ("Jax" for "Jaxon").
 *  Two is too short — "Ca" would catch half the alphabet of names. */
const MIN_NICKNAME_LEN = 3;

/**
 * Prefixes that are ordinary words, so they are never a nickname.
 *
 * ⚠️ There is no nickname STORE anywhere in Kin, so "Jax for Jaxon" has to be
 * approximated by prefix matching — and prefix matching alone said "Car
 * pickup" was about Carson, which is exactly the wrong-confident-guess this
 * file exists to avoid. These are the collisions a school calendar actually
 * produces.
 *
 * ⚠️ KNOWN LIMIT, stated rather than hidden: this is a blocklist, so an
 * unlisted word is ALLOWED, and that is the risky direction — a kid whose name
 * begins with an ordinary word that is not listed here can still be matched by
 * it. No example is given on purpose: the obvious ones are all in the list, so
 * any example written here would be false the moment it was read, which is
 * worse than none.
 *
 * What keeps the limit cheap is that a match only ever PRESELECTS a chip she
 * can see and tap, and the possessive route never consults this list at all.
 */
const NICKNAME_NOT_A_NAME = new Set([
  "car", "cars", "cal", "can", "cam", "cat", "cut", "cup", "class",
  "jan", "jam", "jar", "job", "jog", "jun", "jul",
  "mar", "may", "man", "map", "mat", "max", "mom", "mon",
  "art", "ban", "bar", "bat", "bus", "bed", "ben", "big", "box", "boy",
  "day", "den", "dad", "dog", "don", "dot", "drop",
  "eve", "end", "exam", "fall", "fee", "few", "fit", "fun",
  "gym", "gap", "get", "got", "gre", "gro",
  "half", "hall", "hat", "hot", "hour", "ice", "kid", "lab", "lap", "late",
  "law", "let", "lot", "low", "lunch", "mid", "net", "new", "non", "not",
  "one", "out", "pan", "par", "pat", "pen", "per", "pet", "pic", "pin",
  "pop", "pre", "pro", "ram", "ran", "rat", "red", "rev", "row", "run",
  "sat", "set", "she", "sit", "six", "son", "sub", "sun", "tab", "tag",
  "tan", "tap", "ten", "the", "tie", "tip", "ton", "top", "toy", "two",
  "van", "war", "was", "way", "web", "wed", "wet", "win", "won",
]);

/** Words from a memory fact that are too generic to identify anyone. */
const MEMORY_STOPWORDS = new Set([
  "school", "practice", "class", "team", "lesson", "lessons", "club", "camp",
  "game", "games", "night", "day", "days", "week", "weekly", "month", "every",
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
  "morning", "afternoon", "evening", "after", "before", "with", "and", "the",
  "from", "this", "that", "their", "there", "year", "grade", "teacher", "coach",
  "soccer", "dance", "swim", "piano", "baseball", "basketball", "gymnastics",
]);

function escapeRx(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Does `needle` appear in `haystack` as a whole word?
 *
 * ⚠️ Hand-built boundaries rather than `\b`, because `\b` is defined against
 * `\w` and an apostrophe is not a word character — so `\bC\b` matches the "C"
 * inside "C's" (good) but `\bJax\b` also matches "Jax" inside "Jax-Ryan"
 * (fine) AND inside "Jaxon" it does not (also fine). The reason for doing it
 * by hand is the LEFT side: `\b` would let "C" match the "c" in "Mc" if the
 * preceding character were punctuation. A letter or digit on either side
 * disqualifies the hit; anything else — space, apostrophe, slash, start, end —
 * allows it.
 */
function containsWord(haystack: string, needle: string): boolean {
  if (!needle) return false;
  const rx = new RegExp(`(^|[^A-Za-z0-9])${escapeRx(needle)}($|[^A-Za-z0-9])`, "i");
  return rx.test(haystack);
}

/** "Carson's" / "Carson’s" / "Carsons" as a whole word. Both apostrophes:
 *  iOS autocorrect produces the curly one and she types on a phone. */
function containsPossessive(haystack: string, name: string): boolean {
  const rx = new RegExp(
    `(^|[^A-Za-z0-9])${escapeRx(name)}(?:['’]s|s['’]|['’]|s)($|[^A-Za-z0-9])`,
    "i",
  );
  return rx.test(haystack);
}

/** Is this word generic, in either its singular or plural form?
 *
 *  ⚠️ The plural check is not tidiness. The stopword list held "tuesday" and
 *  a real fact says "on Tuesdays", so "Tuesdays" sailed through as a
 *  distinctive word and any import mentioning a Tuesday matched that kid. */
function isGenericWord(word: string): boolean {
  const lower = word.toLowerCase();
  if (MEMORY_STOPWORDS.has(lower)) return true;
  return lower.endsWith("s") && MEMORY_STOPWORDS.has(lower.slice(0, -1));
}

/** The distinctive words of a memory fact — long enough and not generic. */
export function memoryKeywords(value: string): string[] {
  return (value || "")
    .split(/[^A-Za-z0-9']+/)
    .map((w) => w.trim())
    .filter((w) => w.length >= 4 && !isGenericWord(w));
}

/** Collapses a candidate set to a single kid, or to nothing. */
function soleKid(hits: Map<string, string>): { kid: string; evidence: string } | null {
  if (hits.size !== 1) return null;
  const [[kid, evidence]] = [...hits.entries()];
  return { kid, evidence };
}

/**
 * Which kid is this text about?
 *
 * Passes run in descending confidence and the FIRST pass that yields exactly
 * one kid wins. A pass that yields two or more stops the whole search rather
 * than falling through to a weaker rule — if "Carson and Caleb" is ambiguous
 * by name, an initial is not going to resolve it, and guessing from a weaker
 * signal after a strong tie is how the wrong kid gets picked.
 */
export function matchKidInText(
  text: string,
  kids: readonly string[],
  facts: readonly KidMemoryFact[] = [],
): KidMatch | null {
  const haystack = (text || "").trim();
  if (!haystack) return null;
  const names = kids.map((k) => (k || "").trim()).filter(Boolean);
  if (names.length === 0) return null;

  // Pass 1 — a possessive of the stored name. The strongest signal there is:
  // "C's School" is about C in a way "C School" is not quite.
  const possessive = new Map<string, string>();
  for (const name of names) {
    if (containsPossessive(haystack, name)) possessive.set(name, `${name}'s`);
  }
  if (possessive.size > 0) {
    const sole = soleKid(possessive);
    return sole ? { ...sole, via: "possessive" } : null;
  }

  // Pass 2 — the stored name as a whole word.
  const byName = new Map<string, string>();
  for (const name of names) {
    if (containsWord(haystack, name)) byName.set(name, name);
  }
  if (byName.size > 0) {
    const sole = soleKid(byName);
    return sole ? { ...sole, via: "name" } : null;
  }

  // Pass 3 — a prefix nickname ("Jax" for "Jaxon"), possessive or plain.
  //
  // ⚠️ Only when that prefix belongs to exactly ONE kid. With Jonah and Joss
  // on the list, "Jo" identifies neither, and the shared-prefix check has to
  // run against the OTHER names rather than against the hit count — two kids
  // whose names both start "Jo" would each contribute their own entry and the
  // size check alone would already catch it, but a nickname that matches one
  // kid while being a prefix of another's name is the case that slips through.
  const byNickname = new Map<string, string>();
  for (const name of names) {
    if (name.length <= MIN_NICKNAME_LEN) continue;
    for (let len = name.length - 1; len >= MIN_NICKNAME_LEN; len -= 1) {
      const nick = name.slice(0, len);
      const sharedWithAnother = names.some(
        (other) => other !== name && other.toLowerCase().startsWith(nick.toLowerCase()),
      );
      if (sharedWithAnother) continue;
      // A possessive is a strong enough signal to stand on its own — "Car's
      // pickup" is not a thing anyone writes. A BARE prefix has to clear the
      // ordinary-word check first.
      if (containsPossessive(haystack, nick)) {
        byNickname.set(name, nick);
        break;
      }
      if (!NICKNAME_NOT_A_NAME.has(nick.toLowerCase()) && containsWord(haystack, nick)) {
        byNickname.set(name, nick);
        break;
      }
    }
  }
  if (byNickname.size > 0) {
    const sole = soleKid(byNickname);
    return sole ? { ...sole, via: "nickname" } : null;
  }

  // Pass 4 — family memory. A school, team or teacher this family has told Kin
  // about, tied to the kid it is about via the fact's `subject`.
  const byMemory = new Map<string, string>();
  for (const fact of facts) {
    const subject = (fact?.subject || "").trim();
    if (!subject) continue;
    const owner = names.find((n) => n.toLowerCase() === subject.toLowerCase());
    if (!owner) continue;
    for (const word of memoryKeywords(fact.value)) {
      if (containsWord(haystack, word)) {
        byMemory.set(owner, word);
        break;
      }
    }
  }
  if (byMemory.size > 0) {
    const sole = soleKid(byMemory);
    return sole ? { ...sole, via: "memory" } : null;
  }

  // Pass 5 — a bare initial, LAST and only when it is unique among the kids.
  //
  // ⚠️ This is the weakest and most dangerous rule in the file: a single
  // letter as a whole word. It is why containsWord exists. A name that IS one
  // letter is already covered by passes 1-2, so this only fires for a longer
  // name referred to by its first letter.
  const byInitial = new Map<string, string>();
  for (const name of names) {
    if (name.length < 2) continue;
    const initial = name.charAt(0);
    const sharedInitial = names.some(
      (other) => other !== name && other.charAt(0).toLowerCase() === initial.toLowerCase(),
    );
    if (sharedInitial) continue;
    if (containsPossessive(haystack, initial) || containsWord(haystack, initial)) {
      byInitial.set(name, initial);
    }
  }
  if (byInitial.size > 0) {
    const sole = soleKid(byInitial);
    return sole ? { ...sole, via: "initial" } : null;
  }

  return null;
}

/** "Looks like this is for C — tap to change." */
export function kidMatchNotice(match: KidMatch): string {
  return `Looks like this is for ${match.kid} — tap to change.`;
}
